import { useMemo } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import Image from "next/image";
import { getContactUaeText } from "../../../locales/contact-uae";

export default function LastSection() {
  const { t, locale } = useI18n();
  const contactUaeText = getContactUaeText(locale);

  const items = useMemo(
    () => contactUaeText?.last_section?.list ?? [],
    [contactUaeText]
  );

  const resolveHref = (info) => {
    if (!info) return null;

    const trimmed = String(info).trim();

    // URL
    if (/^https?:\/\//i.test(trimmed)) return trimmed;

    // Email
    if (trimmed.includes("@")) return `mailto:${trimmed}`;

    // Phone (allow +, spaces, -, parentheses)
    const phoneLike = trimmed.replace(/[()\s-]/g, "");
    if (/^\+?\d{7,15}$/.test(phoneLike)) return `tel:${trimmed}`;

    return null;
  };

  return (
    <Box className="horizontal-safe-padding vertical-safe-padding">
      {/* Title + Divider */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: { xs: "16px", sm: "4px" },
        }}
      >
        <Box
          component="h3"
          sx={{
            minHeight: { xs: "42px", sm: "57px" }, // stable height => less CLS
            fontWeight: 700,
            fontSize: { xs: "24px", sm: "32px" },
            lineHeight: { xs: "32px", sm: "57px" },
            color: "#2D317F",
            m: 0,
            textAlign: "center",
          }}
        >
          {contactUaeText.last_section.contact_us}
        </Box>

        {/* Divider as next/image (fixed dims => less CLS) */}
        <Box sx={{ width: { xs: "128.5px", sm: "180px" }, height: { xs: "5px", sm: "7px" }, position: "relative" }}>
          <Image
            src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/divider-2.webp"
            alt="Divider img"
            fill
            sizes="(max-width: 600px) 128px, 180px"
            style={{ objectFit: "contain" }}
          />
        </Box>
      </Box>

      {/* Cards */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "center", sm: "start" },
          justifyContent: { md: "space-between" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 5, md: 0 },
          mt: 5,
        }}
      >
        {items.map((item, index) => {
          const href = resolveHref(item?.info);

          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                width: { lg: "355px" },
                gap: "20px",
              }}
            >
              <Image
                src={item.img}
                alt={item.title}
                width={40}
                height={40}
                quality={85}
              />

              <Box
                component="h4"
                sx={{
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: "20px",
                  color: "#111827",
                  m: 0,
                }}
              >
                {item.title}
              </Box>

              <Box
                sx={{
                  textAlign: "center",
                  fontWeight: 300,
                  fontSize: "16px",
                  lineHeight: "24px",
                  color: "#4B5563",
                }}
              >
                {item.description}
              </Box>

              {/* Clickable only when we can infer href */}
              {href ? (
                <MuiLink
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#2D317F",
                    textDecoration: "underline",
                  }}
                >
                  {item.info}
                </MuiLink>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#2D317F",
                    textDecoration: "underline",
                  }}
                >
                  {item.info}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Social icons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row-reverse",
          alignItems: "center",
          justifyContent: "center",
          height: "32px",
          gap: "15.29px",
          marginTop: 5,
        }}
      >
        {[
          {
            href: "https://x.com/contact_eg?s=21&t=G4mL-Jgpi9cT9jED7aRiVw",
            src: "/t.svg",
            alt: "x",
            label: "X",
          },
          {
            href: "https://www.linkedin.com/company/contact-uae/about/",
            src: "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/l.svg",
            alt: "linkedIn",
            label: "LinkedIn",
          },
          {
            href: "https://www.youtube.com/@ContactUAE",
            src: "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/y.svg",
            alt: "youtube",
            label: "YouTube",
          },
          {
            href: "https://www.instagram.com/uaecontact/",
            src: "/i.svg",
            alt: "instagram",
            label: "Instagram",
          },
          {
            href: "https://www.facebook.com/people/Contact-UAE/61562912941430/",
            src: "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/f.svg",
            alt: "facebook",
            label: "Facebook",
          },
        ].map((s) => (
          <MuiLink
            key={s.href}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={s.label}
            sx={{ display: "inline-flex" }}
          >
            <Image
              src={s.src}
              alt={s.alt}
              width={32}
              height={32}
              priority={false}
            />
          </MuiLink>
        ))}
      </Box>
    </Box>
  );
}
